/**
 * TeamVacationCalendar.js
 *
 * Monthly calendar grid component that displays vacation days with yellow backgrounds,
 * staff initials, and person counts. Clicking a day opens the VacationDayPanel.
 *
 * FIXED: Date timezone conversion bug - now uses local date strings
 * FIXED: All hardcoded German strings replaced with i18n translations
 *
 * NOTE: This component is already compatible with minutes-based vacation durations.
 * The calendar grid displays staff initials and counts, not duration values.
 * Duration formatting happens in VacationDayPanel when a day is clicked.
 *
 * HOLIDAY INTEGRATION: Displays federal holidays with purple backgrounds,
 * overlaying vacation information when both exist on same day.
 *
 * RESPONSIVE: Avatar count is capped at 2 on screens narrower than 480px.
 * The grid sits inside a scroll wrapper so very narrow viewports can scroll
 * horizontally rather than collapsing cells below a usable size.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import VacationDayPanel from './VacationDayPanel';
import '../../styles/vacation.css';

/**
 * Returns true while the given CSS media query matches the current viewport.
 * Defaults to false during SSR when window is unavailable.
 */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const listener = (e) => setMatches(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]);

  return matches;
};

const TeamVacationCalendar = ({
  currentMonth,
  vacations = [],
  staff = [],
  departments = [],
  selectedDepartmentId = 'all',
  selectedStaffId = 'all',
  holidays = []
}) => {
  const { t, i18n } = useTranslation('vacation');
  const [selectedDate, setSelectedDate] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  /**
   * Cap displayed initials at 2 on screens narrower than 480px, 4 otherwise.
   * This prevents avatar badges from wrapping awkwardly inside compact cells.
   */
  const isSmallScreen = useMediaQuery('(max-width: 480px)');
  const maxAvatars = isSmallScreen ? 2 : 4;

  /**
   * Convert Date object to YYYY-MM-DD string using LOCAL time.
   * This avoids timezone conversion issues.
   */
  const formatDateToString = useCallback((date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  /**
   * Find holiday for a specific date.
   * Returns holiday object if exists, null otherwise.
   */
  const getHolidayForDate = useCallback((date) => {
    if (!date || !holidays || holidays.length === 0) return null;
    const dateStr = formatDateToString(date);
    return holidays.find(h => h.dateString === dateStr) || null;
  }, [holidays, formatDateToString]);

  /**
   * Get days in month.
   * Returns array of Date objects for each day in the month.
   */
  const getDaysInMonth = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  }, []);

  /**
   * Get the starting day of week for the month.
   * Adjusted for Monday-first calendar (Monday = 0, Sunday = 6).
   */
  const getFirstDayOfMonth = useCallback((date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    let dayOfWeek = firstDay.getDay();
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  }, []);

  /**
   * Generate calendar grid with empty cells for alignment.
   */
  const calendarDays = useMemo(() => {
    const days = getDaysInMonth(currentMonth);
    const firstDayOfWeek = getFirstDayOfMonth(currentMonth);
    const emptyDays = Array(firstDayOfWeek).fill(null);
    return [...emptyDays, ...days];
  }, [currentMonth, getDaysInMonth, getFirstDayOfMonth]);

  /**
   * Group vacations by date string (YYYY-MM-DD).
   */
  const vacationsByDate = useMemo(() => {
    const grouped = new Map();
    vacations.forEach(vacation => {
      const dateStr = vacation.getDateString ? vacation.getDateString() : vacation.date;
      if (!dateStr) {
        console.warn('Vacation missing date:', vacation);
        return;
      }
      if (!grouped.has(dateStr)) {
        grouped.set(dateStr, []);
      }
      grouped.get(dateStr).push(vacation);
    });
    return grouped;
  }, [vacations]);

  /**
   * Get vacations for a specific date.
   */
  const getVacationsForDate = useCallback((date) => {
    if (!date) return [];
    const dateStr = formatDateToString(date);
    return vacationsByDate.get(dateStr) || [];
  }, [vacationsByDate, formatDateToString]);

  /**
   * Get staff initials from name.
   */
  const getInitials = useCallback((name) => {
    if (
      !name ||
      name === 'Lädt...' ||
      name === 'Unbekannter Mitarbeiter' ||
      name === 'Gelöschter Mitarbeiter'
    ) {
      return '??';
    }
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, []);

  /**
   * Get staff initials for a specific date.
   * The number of displayed initials is capped by maxAvatars, which is
   * 2 on screens narrower than 480px and 4 otherwise.
   * maxDisplay is returned in the result so the render section can compute
   * the overflow count without referencing the outer variable directly.
   */
  const getStaffInitialsForDate = useCallback((date) => {
    const dayVacations = getVacationsForDate(date);
    if (dayVacations.length === 0) return null;

    const uniqueStaffNames = [...new Set(
      dayVacations.map(v => v.staffName || 'Unknown')
    )];
    const initials = uniqueStaffNames.map(name => getInitials(name));

    return {
      displayed: initials.slice(0, maxAvatars),
      total: initials.length,
      hasMore: initials.length > maxAvatars,
      maxDisplay: maxAvatars
    };
  }, [getVacationsForDate, getInitials, maxAvatars]);

  /**
   * Handle day click - opens panel if there are vacations or a holiday.
   */
  const handleDayClick = useCallback((date) => {
    if (!date) return;
    const dayVacations = getVacationsForDate(date);
    const holiday = getHolidayForDate(date);

    if (dayVacations.length === 0 && !holiday) return;

    console.log(
      'Day clicked:',
      date.toLocaleDateString(i18n.language),
      'Vacations:', dayVacations.length,
      'Holiday:', holiday ? holiday.name : 'none'
    );
    setSelectedDate(date);
    setIsPanelOpen(true);
  }, [getVacationsForDate, getHolidayForDate, i18n.language]);

  /**
   * Handle panel close.
   */
  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedDate(null);
  }, []);

  /**
   * Check if date is today.
   */
  const isToday = useCallback((date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  /**
   * Check if date is in current month.
   */
  const isCurrentMonth = useCallback((date) => {
    if (!date) return false;
    return (
      date.getMonth() === currentMonth.getMonth() &&
      date.getFullYear() === currentMonth.getFullYear()
    );
  }, [currentMonth]);

  /**
   * Weekday headers - generated dynamically from current language.
   * Uses Jan 5, 2015 (a Monday) as reference week start.
   */
  const weekDays = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) =>
      formatter.format(new Date(2015, 0, 5 + i))
    );
  }, [i18n.language]);

  return (
    <div className="team-vacation-calendar">
      {/*
        calendar-scroll-wrapper enables horizontal scrolling on very narrow
        viewports. The inner .calendar-grid receives a min-width via CSS so
        cells never collapse below a legible size. On desktop the wrapper is
        invisible — no overflow, no scrollbar.
      */}
      <div className="calendar-scroll-wrapper">
        <div className="calendar-grid">

          {/* Weekday Headers */}
          {weekDays.map((day, index) => (
            <div key={`header-${index}`} className="calendar-header">
              {day}
            </div>
          ))}

          {/* Calendar Days */}
          {calendarDays.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="calendar-day empty" />;
            }

            const dayVacations = getVacationsForDate(date);
            const holiday = getHolidayForDate(date);
            const hasVacations = dayVacations.length > 0;
            const hasHoliday = holiday !== null;
            const staffInitials = getStaffInitialsForDate(date);
            const isTodayDate = isToday(date);
            const isInCurrentMonth = isCurrentMonth(date);

            const cssClasses = [
              'calendar-day',
              hasHoliday && 'holiday',
              hasVacations && 'has-vacation',
              isTodayDate && 'today',
              !isInCurrentMonth && 'other-month'
            ].filter(Boolean).join(' ');

            return (
              <div
                key={formatDateToString(date)}
                className={cssClasses}
                onClick={() => handleDayClick(date)}
                role="button"
                tabIndex={(hasVacations || hasHoliday) ? 0 : -1}
                aria-label={
                  date.toLocaleDateString(i18n.language, {
                    day: 'numeric',
                    month: 'long'
                  }) +
                  (hasHoliday ? `, ${holiday.name}` : '') +
                  (hasVacations
                    ? `, ${dayVacations.length} ${t('staffOnVacation')}`
                    : '')
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleDayClick(date);
                  }
                }}
              >
                {/* Day Number */}
                <div className="day-number">
                  {date.getDate()}
                </div>

                {/* Holiday Name */}
                {hasHoliday && (
                  <div className="holiday-info">
                    <div className="holiday-name">{holiday.name}</div>
                  </div>
                )}

                {/* Vacation Info */}
                {hasVacations && staffInitials && (
                  <div className="vacation-info">

                    {/* Staff Initials */}
                    <div className="staff-initials">
                      {staffInitials.displayed.map((initial, idx) => (
                        <span key={idx} className="initial-badge">
                          {initial}
                        </span>
                      ))}
                      {staffInitials.hasMore && (
                        <span className="initial-badge more">
                          +{staffInitials.total - staffInitials.maxDisplay}
                        </span>
                      )}
                    </div>

                    {/* Person Count - uses i18next plural rules */}
                    <div className="person-count">
                      {t('personCount', { count: dayVacations.length })}
                    </div>

                  </div>
                )}
              </div>
            );
          })}

        </div>
      </div>

      {/* Vacation Day Panel */}
      {selectedDate && (
        <VacationDayPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          date={selectedDate}
          vacations={getVacationsForDate(selectedDate)}
          holiday={getHolidayForDate(selectedDate)}
          staff={staff}
          departments={departments}
        />
      )}
    </div>
  );
};

export default TeamVacationCalendar;